//
// Copyright (c) 2024 Emplifi, Inc.
//

// @ts-ignore
import "../css/snippetSelect.css";
import {
  ButtonView,
  CssTransitionDisablerMixin,
  type Editor,
  IconCancel,
  IconCheck,
  submitHandler,
  View,
  ViewWithCssTransitionDisabler,
} from "ckeditor5";
// @ts-ignore
import i18next from "i18n/i18n";
import { removeCkReset } from "../../ckEditorUtil";
import SnippetSelect from "./snippetSelect";
import type SnippetCommand from "../snippetCommand";

class BaseSnippetView extends View {
  private readonly cancelButton: ButtonView;
  private readonly editor: Editor;
  private readonly okButton: ButtonView;
  private readonly snippetSelectView: SnippetSelect;

  constructor(editor: Editor, command: SnippetCommand) {
    super(editor.locale);
    this.editor = editor;
    this.snippetSelectView = this._createSnippetSelectView(command);
    this.okButton = this._createOkButton();
    this.cancelButton = this._createCancelButton();
    this.setTemplate({
      tag: "form",
      attributes: {
        class: ["ck", "ck-responsive-form", "ck-vertical-form", "snippet-select"],
        tabindex: "-1",
      },
      children: this._createFormChildren(),
    });
  }

  get snippetId() {
    if (this.snippetSelectView) {
      return this.snippetSelectView.snippetId;
    }
    return null;
  }

  get snippetName() {
    if (this.snippetSelectView) {
      return this.snippetSelectView.snippetName;
    }
    return null;
  }

  focus() {
    if (this.snippetSelectView) {
      this.snippetSelectView.focus();
    }
  }

  render() {
    super.render();
    // Remove the CKEditor reset CSS classes to better integrate with Marionette UI
    setTimeout(() => removeCkReset(this.element), 100);
    submitHandler({
      view: this,
    });
  }

  _createCancelButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.cancel"),
      icon: IconCancel,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-cancel",
      },
    });
    button.delegate("execute").to(this, "cancel");
    return button;
  }

  _createFormChildren() {
    const children = this.createCollection();
    children.add(this.snippetSelectView);
    children.add(this.okButton);
    children.add(this.cancelButton);
    return children;
  }

  _createOkButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.ok"),
      icon: IconCheck,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-save",
      },
    });
    button.type = "submit";
    button.delegate("execute").to(this, "submit");
    return button;
  }

  _createSnippetSelectView(command: SnippetCommand) {
    const view = new SnippetSelect(this.editor, this.locale);
    view.snippetId = command.value;
    view.bind("snippetId").to(command, "value");
    return view;
  }
}

export const SnippetView = CssTransitionDisablerMixin(BaseSnippetView);
export type SnippetViewType = BaseSnippetView & ViewWithCssTransitionDisabler;
